import type { BadgeVariant } from '@/components/ui/Badge';
import type { AuditTrailEvent, AuditTrailSource } from '@/api/auditTrailApi';

export const AUDIT_SOURCE_OPTIONS: { id: AuditTrailSource; label: string }[] = [
  { id: 'user', label: 'User' },
  { id: 'workspace', label: 'Workspace' },
  { id: 'automation', label: 'Automation' },
  { id: 'chat', label: 'Chat' },
  { id: 'hourly_budget', label: 'Hourly budget' },
  { id: 'bid_snapshot', label: 'Bid snapshot' },
];

const SOURCE_LABELS: Record<string, string> = Object.fromEntries(
  AUDIT_SOURCE_OPTIONS.map((item) => [item.id, item.label])
);

export function auditSourceLabel(source: string | null | undefined, fallback?: string | null): string {
  const key = (source || '').trim();
  return fallback?.trim() || SOURCE_LABELS[key] || key || 'Unknown';
}

export function auditSourceBadgeVariant(source: string | null | undefined): BadgeVariant {
  switch (source) {
    case 'automation':
    case 'hourly_budget':
      return 'warning';
    case 'workspace':
      return 'neutral';
    case 'bid_snapshot':
      return 'success';
    case 'chat':
    case 'user':
    default:
      return 'brand';
  }
}

export function auditActorLabel(event: Pick<AuditTrailEvent, 'actorEmail' | 'sourceLabel' | 'source'>): string {
  if (event.actorEmail?.trim()) return event.actorEmail.trim();
  if (event.sourceLabel?.trim()) return event.sourceLabel.trim();
  return auditSourceLabel(event.source);
}

export function auditEntityLabel(
  event: Pick<AuditTrailEvent, 'entityType' | 'entityName' | 'entityId'>
): string {
  const type = (event.entityType || 'entity').replace(/_/g, ' ');
  const name = event.entityName?.trim() || event.entityId?.trim();
  return name ? `${type} · ${name}` : type;
}

export function auditStatusLabel(status: string | null | undefined): string {
  const value = (status || '').trim().toLowerCase();
  if (value === 'succeeded' || value === 'success') return 'Succeeded';
  if (value === 'failed' || value === 'error') return 'Failed';
  return status?.trim() || 'Unknown';
}

function asFiniteNumber(value: unknown): number | string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : value.trim();
  }
  return null;
}

export function budgetAmountFromPayload(value: unknown): number | string | null {
  if (value == null || typeof value !== 'object') return asFiniteNumber(value);
  const item = value as Record<string, unknown>;
  const budget = item.budget;
  const fromBudget = asFiniteNumber(budget);
  if (fromBudget != null) return fromBudget;
  if (budget && typeof budget === 'object') {
    const nested = budget as Record<string, unknown>;
    for (const key of ['amount', 'budget'] as const) {
      const nestedAmount = asFiniteNumber(nested[key]);
      if (nestedAmount != null) return nestedAmount;
    }
  }
  return asFiniteNumber(item.dailyBudget);
}

function payloadState(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const state = (value as Record<string, unknown>).state;
  if (typeof state === 'string' && state.trim()) return state.trim();
  return null;
}

function formatAuditAmount(value: number | string): string {
  return String(value);
}

export function auditChangeLabel(
  event: Pick<AuditTrailEvent, 'summary' | 'action' | 'before' | 'after'>
): string {
  const afterBudget = budgetAmountFromPayload(event.after);
  const beforeBudget = budgetAmountFromPayload(event.before);
  if (afterBudget != null) {
    const parts: string[] = [];
    if (beforeBudget != null) {
      parts.push(`budget ${formatAuditAmount(beforeBudget)} → ${formatAuditAmount(afterBudget)}`);
    } else {
      parts.push(`budget → ${formatAuditAmount(afterBudget)}`);
    }
    const state = payloadState(event.after);
    if (state) parts.push(`state → ${state}`);
    return parts.join('; ');
  }
  return event.summary?.trim() || event.action.replace(/_/g, ' ');
}
